import {
  Menu,
  IconButton,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { Image, Inspect, List, ListOrdered, MessageSquare, Paperclip } from 'lucide-react';
import { Editor, JSONContent } from '@tiptap/react';
import {
  Edge,
  Node,
  getConnectedEdges,
  useOnViewportChange,
  useReactFlow,
  useUpdateNodeInternals,
} from 'reactflow';
import { NodeData, chatMessageDBlock } from '../flowEditorBlockNodeTypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createUploadDBlock, messageJSON, olJSON, ulJSON } from '../../tiptap/contentJSONs';
import { createId } from '@paralleldrive/cuid2';

import { EditLinkModalButton, EditLinkMenuItem } from '../../../EditLinkModalButton';
import YoutubeMenu, { YoutubeMenuItem } from './YoutubeMenu';
import AddGifButton from './AddGifButton';
import { AnchorRect, rectToAnchor } from './AnchoredPanel';

export function FlowActionMenu({
  node,
  editor,
  nodeData,
  setNodeData,
  updateContent,
}: {
  node: Node;
  editor: Editor;
  nodeData: NodeData;
  // Applies the new node data, marks the flow dirty (so it autosaves) and takes
  // an undo snapshot. Always passed a fully-built NodeData, never a functional
  // updater.
  setNodeData: (data: NodeData) => void;
  updateContent: (newContent: JSONContent) => void;
}) {
  const [menuOpened, setMenuOpened] = useState(false);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(650);
  // Link/YouTube panels are owned here, not inside the (lazy, close-on-select)
  // MenuList, so opening them doesn't immediately unmount them. Open on the next
  // frame so the menu's close (and its focus return) doesn't register as an
  // outside interaction that dismisses the just-opened panel. The captured
  // anchor positions the panel where the menu was.
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const linkModal = useDisclosure();
  const youtubeModal = useDisclosure();
  const [linkAnchor, setLinkAnchor] = useState<AnchorRect | null>(null);
  const [youtubeAnchor, setYoutubeAnchor] = useState<AnchorRect | null>(null);
  // Anchor every panel to the "+" button element itself (its real on-screen
  // rect), so they all open in the same spot beside the button regardless of
  // which way the menu expanded.
  const openLinkModal = useCallback(() => {
    setLinkAnchor(rectToAnchor(menuButtonRef.current));
    requestAnimationFrame(linkModal.onOpen);
  }, [linkModal]);
  const openYoutubeModal = useCallback(() => {
    setYoutubeAnchor(rectToAnchor(menuButtonRef.current));
    requestAnimationFrame(youtubeModal.onOpen);
  }, [youtubeModal]);
  const { getNodes, getEdges, deleteElements } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  useOnViewportChange({
    onStart: useCallback(() => {
      setMenuOpened(false);
    }, []),
  });

  const calculateMaxHeight = () => {
    const flowScreenSize = window.innerHeight - 100;

    const newMaxHeight = Math.min(flowScreenSize, 650);
    setDynamicMaxHeight(newMaxHeight);
  };

  useEffect(() => {
    calculateMaxHeight();
    if (menuOpened) {
      // Add event listener if menu is open
      window.addEventListener('resize', calculateMaxHeight);
    } else {
      // Remove event listener if menu is closed
      window.removeEventListener('resize', calculateMaxHeight);
    }
    return () => {
      // Cleanup: remove event listener when component unmounts
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, [menuOpened]);
  const nodeId = node.id;

  return (
    <>
      <Menu
        onOpen={() => {
          setMenuOpened(true);
        }}
        onClose={() => setMenuOpened(false)}
        placement="right-start"
        isOpen={menuOpened}
        // Don't mount the (portalled) menu list until the menu is opened. Without
        // this Chakra renders it off-screen even while closed, so it re-rendered
        // on every keystroke of the node being edited and painted over the page
        // chrome.
        isLazy
      >
        <MenuButton
          ref={menuButtonRef}
          as={IconButton}
          isRound={true}
          size="xs"
          colorScheme="gray"
          variant="outline"
          aria-label="Add"
          bgColor="gray.100"
          onClick={(ev) => {
            setMenuOpened(!menuOpened);
            ev.stopPropagation();
            ev.preventDefault();
          }}
          icon={<AddIcon w="13px" color="gray.700" />}
        />
        <Portal>
          <MenuList
            overflowY="auto"
            overflowX="hidden"
            mb="20px"
            maxHeight={`${dynamicMaxHeight}px`}
          >
            <MenuGroup color="gray.400" title="TEXT AND LAYOUT">
              <MenuItem
                onClick={() => updateContent(chatMessageDBlock(messageJSON))}
                icon={<MessageSquare size="16px" />}
              >
                Message
              </MenuItem>
              <MenuItem
                onClick={() => updateContent(chatMessageDBlock(ulJSON))}
                icon={<List size="16px" />}
              >
                Bulleted list
              </MenuItem>
              <MenuItem
                onClick={() => updateContent(chatMessageDBlock(olJSON))}
                icon={<ListOrdered size="16px" />}
              >
                Numbered list
              </MenuItem>
              <EditLinkMenuItem onOpen={openLinkModal} />
            </MenuGroup>
            <MenuGroup color="gray.400" title="MEDIA AND CONTENT">
              <MenuItem
                onClick={() => updateContent(createUploadDBlock('image/*'))}
                icon={<Image size="16px" />}
              >
                Image
              </MenuItem>
              <AddGifButton
                targetNode={node}
                getAnchor={() => rectToAnchor(menuButtonRef.current)}
              />
              <YoutubeMenuItem onOpen={openYoutubeModal} />
              <MenuItem
                onClick={() => updateContent(createUploadDBlock())}
                icon={<Paperclip size="16px" />}
              >
                File
              </MenuItem>
            </MenuGroup>
            {nodeData.reply || nodeData.options || nodeData.quizId ? null : (
              <MenuGroup color="gray.400" title="RESPONSE">
                <MenuItem
                  onClick={() => {
                    let outgoerToRemove: Edge | undefined;
                    //NOTE: If we have connected node, remove edge (also from outgoers)
                    const currentNode = getNodes().find((n) => n.id === nodeId);
                    if (currentNode) {
                      const connectedEdges = getConnectedEdges([currentNode], getEdges());
                      outgoerToRemove = connectedEdges.find((e) => e.source === nodeId);

                      if (outgoerToRemove) {
                        deleteElements({ nodes: [], edges: [outgoerToRemove] });
                      }
                    }

                    setNodeData({
                      ...nodeData,
                      options: [
                        { id: createId(), title: 'Option 1' },
                        { id: createId(), title: 'Option 2' },
                      ],
                    });

                    updateNodeInternals(nodeId);
                  }}
                  icon={<Inspect size="16px" />}
                >
                  Reply buttons
                </MenuItem>
              </MenuGroup>
            )}
          </MenuList>
        </Portal>
      </Menu>
      <EditLinkModalButton
        editor={editor}
        updateContent={updateContent}
        isOpen={linkModal.isOpen}
        onClose={linkModal.onClose}
        anchorRect={linkAnchor}
      />
      <YoutubeMenu
        targetNode={node}
        isOpen={youtubeModal.isOpen}
        onClose={youtubeModal.onClose}
        anchorRect={youtubeAnchor}
      />
    </>
  );
}
